import { useEffect, useRef, type RefObject } from 'react';
import type { IslandRenderer, IslandVisual } from '../scene/types';

/** One canvas for every island. Accessible chest buttons remain in the DOM. */
export function MilestoneWorld({
  roadmap,
  items,
}: {
  roadmap: RefObject<HTMLDivElement | null>;
  items: IslandVisual[];
}) {
  const host = useRef<HTMLDivElement>(null);
  const controller = useRef<IslandRenderer | null>(null);
  const latest = useRef(items);
  useEffect(() => {
    latest.current = items;
    controller.current?.update(items);
  }, [items]);
  useEffect(() => {
    let disposed = false;
    if (!host.current || !roadmap.current || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        void import('../scene/create-milestone-world')
          .then(({ createMilestoneWorld }) => {
            if (disposed || !host.current || !roadmap.current) return;
            controller.current = createMilestoneWorld(host.current, roadmap.current);
            controller.current.update(latest.current);
          })
          .catch(() => {
            /* HTML island silhouettes and every action remain usable. */
          });
      },
      { rootMargin: '200px' },
    );
    observer.observe(roadmap.current);
    return () => {
      disposed = true;
      observer.disconnect();
      controller.current?.dispose();
      controller.current = null;
    };
  }, [roadmap]);
  return <div ref={host} className="milestone-world" aria-hidden="true" />;
}
